import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, Checkbox, Button, message, Avatar } from 'antd';
import apiClient from '../server/server';

const { TextArea } = Input;
const { Option } = Select;

const TrackFormModal = ({ 
  visible, 
  onCancel, 
  form, 
  musicGenres, 
  selectedProfile,
  profiles,
  profilesLoading,
  searchProfiles
}) => {
  // Search state for affiliated profiles
  const [searchResults, setSearchResults] = useState(profiles || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Debounced search function
  const debouncedSearch = useCallback(async (searchTerm) => {
    if (!searchProfiles) return;
    
    setSearchLoading(true);
    try {
      const results = await searchProfiles(searchTerm);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching profiles:', error);
      message.error('Failed to search profiles');
    } finally {
      setSearchLoading(false);
    }
  }, [searchProfiles]);

  const handleProfileSearch = (value) => {
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If empty search, show initial profiles
    if (!value || value.trim() === '') {
      setSearchResults(profiles || []);
      setSearchLoading(false);
      return;
    }
    
    // Set loading state immediately
    setSearchLoading(true);
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      debouncedSearch(value.trim());
    }, 300); // 300ms debounce
    
    setSearchTimeout(timeout);
  };

  // Update search results when initial profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0 && searchResults.length === 0) {
      setSearchResults(profiles);
    }
  }, [profiles]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);
  
  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };
  
  const handleSubmit = async (values) => {
    try {
      console.log('🎵 [Track Form] Submitting form values:', values);
      
      // Validate required fields
      if (!values.title || !values.artist || !values.musicTypes || values.musicTypes.length === 0) {
        message.error("Please fill in all required fields (Title, Artist, Music Types)");
        return;
      }

      if (!selectedProfile?._id) {
        message.error("Please select a profile first");
        return;
      }

      // Prepare the track data using the correct API structure
      const payload = {
        // Required fields
        title: values.title,
        artist: values.artist,
        musicTypes: values.musicTypes,
        creatorProfileId: selectedProfile._id,
        
        // Optional fields
        flyer: values.cover || null, // Cover image URL
        buyLink: values.buyLink || null,
        streamLink: values.streamLink || null,
        label: values.label || null,
        releaseDate: values.releaseDate ? values.releaseDate.toISOString() : null,
        duration: values.duration || null,
        trackType: values.trackType || null,
        originCountry: values.originCountry || null,
        isTrending: values.isTrending || false,
        
        // Social & Mood fields
        moodTags: values.moodTags || [],
        affiliatedProfiles: values.affiliatedProfiles || [],
      };

      // Remove null values to clean up the payload
      Object.keys(payload).forEach(key => {
        if (payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      console.log('🎵 [Track Form] Final API payload:', payload);

      const response = await apiClient.post('/tracks/backoffice/create', payload);
      
      if (response && response.data) {
        message.success('Track created successfully!');
        handleCancel(); // Close modal and reset form
      }
    } catch (error) {
      console.error('🎵 [Track Form] Error creating track:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create track';
      message.error('Failed to create track: ' + errorMessage);
    }
  };

  return (
    <Modal
      title="Create Track Publication"
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item name="title" label="Track Title" rules={[{ required: true, message: 'Please enter track title' }]}>
            <Input placeholder="Enter track title" />
          </Form.Item>
          
          <Form.Item name="artist" label="Artist" rules={[{ required: true, message: 'Please enter artist name' }]}>
            <Input placeholder="Enter artist name" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item 
            name="musicTypes" 
            label="Music Types" 
            rules={[{ required: true, message: 'Please select at least one music type' }]}
          >
            <Select mode="multiple" placeholder="Select music types">
              {musicGenres.map(genre => (
                <Option key={genre._id} value={genre._id}>
                  {genre.value}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="label" label="Record Label">
            <Input placeholder="Enter record label" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item name="releaseDate" label="Release Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="duration" label="Duration (seconds)">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="Track duration in seconds" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item name="buyLink" label="Buy Link">
            <Input placeholder="Purchase/iTunes link" />
          </Form.Item>

          <Form.Item name="streamLink" label="Stream Link">
            <Input placeholder="Preview/streaming link" />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item name="cover" label="Cover Image URL">
            <Input placeholder="Track cover image URL" />
          </Form.Item>

          <Form.Item name="trackType" label="Track Type">
            <Select placeholder="Select track type">
              <Option value="Single">Single</Option>
              <Option value="Album">Album</Option>
              <Option value="EP">EP</Option>
              <Option value="Mixtape">Mixtape</Option>
            </Select>
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item name="originCountry" label="Origin Country">
            <Input placeholder="Country of origin" />
          </Form.Item>

          <Form.Item name="isTrending" valuePropName="checked">
            <Checkbox>Trending Track</Checkbox>
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          <Form.Item name="moodTags" label="Mood Tags">
            <Select 
              mode="tags" 
              placeholder="Add mood tags (e.g., energetic, chill, upbeat)"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          <Form.Item name="affiliatedProfiles" label="Affiliated Profiles">
            <Select
              mode="multiple"
              placeholder="Search and select affiliated profiles"
              loading={profilesLoading || searchLoading}
              showSearch
              onSearch={handleProfileSearch}
              filterOption={false} // Disable client-side filtering since we use server-side search
              style={{ width: '100%' }}
              options={searchResults
                .filter(profile => profile._id !== selectedProfile?._id) // Don't include creator profile
                .map(profile => ({
                  value: profile._id,
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar 
                        src={profile.picture} 
                        size={24} 
                        style={{ marginRight: 8 }}
                      />
                      <span>{profile.username}</span>
                    </div>
                  ),
                }))}
            />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
          <Button onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit">
            Create Track Publication
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default TrackFormModal; 